"use client";

import { Suspense, useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

import NavBar from "@/app/components/NavBar";
import Footer from "@/app/components/Footer";
import { stringOrFallback } from "@/app/pages/lib/contentHelpers";

interface PaymentCallbackClientProps {
  locale: string;
  content: Record<string, unknown>;
}

function PaymentCallbackContent({ locale, content }: PaymentCallbackClientProps) {
  const t = (key: string, fallback: string) => stringOrFallback(content[key], fallback);
  const searchParams = useSearchParams();
  const router = useRouter();
  const [status, setStatus] = useState<"success" | "error" | "loading">("loading");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const paymentStatus = searchParams.get("status");
    const paymentId = searchParams.get("paymentId");
    const externalId = searchParams.get("externalId");

    const successMessage = stringOrFallback(content["successMessage"], "Płatność została pomyślnie zrealizowana. Dziękujemy!");
    const toastSuccess = stringOrFallback(content["toastSuccess"], "Płatność zrealizowana");
    const errorMessageRejected = stringOrFallback(content["errorMessageRejected"], "Płatność została odrzucona. Spróbuj ponownie.");
    const toastError = stringOrFallback(content["toastError"], "Płatność odrzucona");
    const errorMessageUnknown = stringOrFallback(content["errorMessageUnknown"], "Status płatności nieznany. Skontaktuj się z obsługą.");
    const toastUnknown = stringOrFallback(content["toastUnknown"], "Nieznany status płatności");

    if (paymentStatus === "CONFIRMED" || paymentStatus === "confirmed" || paymentStatus === "success") {
      setStatus("success");
      setMessage(successMessage);
      toast.success(toastSuccess);

      if (paymentId || externalId) {
        verifyPayment(paymentId, externalId);
      }
    } else if (paymentStatus === "REJECTED" || paymentStatus === "declined" || paymentStatus === "error") {
      setStatus("error");
      setMessage(errorMessageRejected);
      toast.error(toastError);
    } else {
      setStatus("error");
      setMessage(errorMessageUnknown);
      toast.error(toastUnknown);
    }
  }, [searchParams, content]);

  const verifyPayment = async (paymentId?: string | null, externalId?: string | null) => {
    try {
      if (!paymentId && !externalId) return;

      const response = await fetch(`/api/payments/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentId,
          externalId,
        }),
      });

      if (!response.ok) {
        console.error("Payment verification failed");
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
    }
  };

  return (
    <div className="flex-grow flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "loading" && (
          <div className="space-y-4">
            <div className="animate-spin">
              <svg className="w-16 h-16 mx-auto text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-gray-600">{t("loadingTitle", "Weryfikacja płatności...")}</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <CheckCircleIcon className="w-20 h-20 text-green-500 mx-auto" />
            <h1 className="text-3xl font-bold text-green-700">{t("successTitle", "Sukces!")}</h1>
            <p className="text-gray-600 text-lg">{message}</p>
            <div className="pt-4 space-y-3">
              <p className="text-sm text-gray-500">{t("successNote", "Wkrótce otrzymasz email z potwierdzeniem rezerwacji.")}</p>
              <button
                onClick={() => router.push(`/${locale}`)}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                {t("successButton", "Powrót na stronę główną")}
              </button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <XCircleIcon className="w-20 h-20 text-red-500 mx-auto" />
            <h1 className="text-3xl font-bold text-red-700">{t("errorTitle", "Błąd!")}</h1>
            <p className="text-gray-600 text-lg">{message}</p>
            <div className="pt-4 space-y-3">
              <button
                onClick={() => router.push(`/${locale}/make-appointment`)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                {t("retryButton", "Spróbuj ponownie")}
              </button>
              <button
                onClick={() => router.push(`/${locale}`)}
                className="w-full bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
              >
                {t("homeButton", "Powrót na stronę główną")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PaymentCallbackClient({ locale, content }: PaymentCallbackClientProps) {
  const t = (key: string, fallback: string) => stringOrFallback(content[key], fallback);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <NavBar locale={locale} />

      <Suspense
        fallback={
          <div className="flex-grow flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center space-y-6">
              <div className="space-y-4">
                <div className="animate-spin">
                  <svg className="w-16 h-16 mx-auto text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-gray-600">{t("loadingFallback", "Ładowanie...")}</p>
              </div>
            </div>
          </div>
        }
      >
        <PaymentCallbackContent locale={locale} content={content} />
      </Suspense>

      <Footer locale={locale} />
    </div>
  );
}
